import { message } from 'antd'
import { PDFDocument } from 'pdf-lib'
import { useCallback, useEffect, useRef, useState } from 'react'
import { type RouterOutput, trpc } from '../../../utils/trpc'
import { formatDeliveryOption } from '../Boxes'
import { generateLabelPdf } from '../utils/generateLabelPdf'

type ExtractedBoxData = {
  barcode: string
  boxData: {
    type: number
    sourceId: number
    workspaceId: number
    deliveryGroupId: number
    boxId: number
  }
  pageNumber: number
}

type UseGenerateInterleavedPdfParams = {
  extractedBoxes: Array<ExtractedBoxData>
  uploadedPdfBytes: ArrayBuffer
  onComplete: () => void
}

type BoxQueryData = RouterOutput['boxes']['getBoxesByIds'][number]

// validates if all boxes are present
const validateBoxes = (
  extractedBoxes: Array<ExtractedBoxData>,
  queriedBoxes: Array<BoxQueryData>
): { isValid: boolean; missingBoxes: Array<ExtractedBoxData> } => {
  if (queriedBoxes.length < extractedBoxes.length) {
    const missingBoxes = extractedBoxes.filter(
      box => !queriedBoxes.find(b => b.serialId === box.boxData.boxId)
    )
    return { isValid: false, missingBoxes }
  }
  return { isValid: true, missingBoxes: [] }
}

// validates that the number of pages matches the number of extracted boxes
const validatePageCount = (
  extractedBoxesCount: number,
  uploadedPdfPageCount: number
): void => {
  if (uploadedPdfPageCount !== extractedBoxesCount) {
    throw new Error(
      `El número de páginas del PDF (${uploadedPdfPageCount}) no coincide con el número de códigos de barras extraídos (${extractedBoxesCount})`
    )
  }
}

// generates the interleaved PDF
const generateInterleavedPdfDocument = async (
  extractedBoxes: Array<ExtractedBoxData>,
  uploadedPdfBytes: ArrayBuffer,
  queriedBoxes: Array<BoxQueryData>
): Promise<Uint8Array> => {
  const uploadedPdf = await PDFDocument.load(uploadedPdfBytes)
  const mergedPdf = await PDFDocument.create()

  // Validate page count matches extracted boxes
  validatePageCount(extractedBoxes.length, uploadedPdf.getPageCount())

  const firstPage = uploadedPdf.getPage(0)
  const { width: pageWidth, height: pageHeight } = firstPage.getSize()

  let processedCount = 0
  for (const extractedBox of extractedBoxes) {
    const boxData = queriedBoxes.find(box => box.serialId === extractedBox.boxData.boxId)

    if (!boxData) {
      throw new Error(
        `No se encontró la caja ${extractedBox.boxData.boxId} de la página ${extractedBox.pageNumber} en la base de datos`
      )
    }

    // Copy the corresponding page from uploaded PDF
    const pageIndex = extractedBox.pageNumber - 1
    if (pageIndex < uploadedPdf.getPageCount()) {
      const [copiedPage] = await mergedPdf.copyPages(uploadedPdf, [pageIndex])
      mergedPdf.addPage(copiedPage)
    }

    // Generate label PDF for this box
    const labelData = {
      sourceId: extractedBox.boxData.sourceId,
      workspaceId: extractedBox.boxData.workspaceId,
      deliveryGroupId: boxData.deliveryGroupId,
      boxId: boxData.serialId,
      boxTypeName: boxData.boxType.name,
      deliveryGroupIndex: boxData.deliveryGroupIndex,
      deliveryGroupTotalBoxes: boxData.deliveryGroup.boxes.length,
      route: boxData.deliveryGroup.currentRoute ?? { day: 0, route: 'SIN RUTA' },
      deliveryOption: formatDeliveryOption(
        boxData.deliveryGroup.deliveryOption,
        boxData.deliveryGroup.deliveryDate.start
      ),
      address: boxData.deliveryGroup.address,
      pageWidth,
      pageHeight
    }

    const labelPdfBytes = await generateLabelPdf(labelData)
    const labelPdf = await PDFDocument.load(labelPdfBytes)

    const [labelPage] = await mergedPdf.copyPages(labelPdf, [0])
    mergedPdf.addPage(labelPage)
    processedCount++
  }

  // Validate final PDF has correct number of pages (2 per box: original + label)
  const expectedPages = extractedBoxes.length * 2
  const actualPages = mergedPdf.getPageCount()
  if (actualPages !== expectedPages) {
    throw new Error(
      `El PDF generado tiene ${actualPages} páginas pero debería tener ${expectedPages} (${extractedBoxes.length} cajas x 2)`
    )
  }

  // Validate all boxes were processed
  if (processedCount !== extractedBoxes.length) {
    throw new Error(
      `Solo se procesaron ${processedCount} de ${extractedBoxes.length} cajas`
    )
  }

  return mergedPdf.save()
}

// downloads the PDF
const downloadPdf = (pdfBytes: Uint8Array, filename: string): void => {
  const blob = new Blob([pdfBytes], { type: 'application/pdf' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  link.click()
  URL.revokeObjectURL(url)
}

export const useGenerateInterleavedPdf = ({
  extractedBoxes,
  uploadedPdfBytes,
  onComplete
}: UseGenerateInterleavedPdfParams) => {
  const [isProcessing, setIsProcessing] = useState(true)
  const hasGeneratedRef = useRef(false)
  const processedDataRef = useRef<string>('')

  const boxIds = extractedBoxes.map(box => box.boxData.boxId)

  const boxesQuery = trpc.boxes.getBoxesByIds.useQuery({
    sourceId: extractedBoxes[0].boxData.sourceId,
    boxIds
  }, {
    enabled: boxIds.length > 0
  })

  const processInterleavedPdf = useCallback(async () => {
    if (!boxesQuery.data) return

    const { isValid, missingBoxes } = validateBoxes(extractedBoxes, boxesQuery.data)
    if (!isValid) {
      message.error(
        `No se encontraron las siguientes cajas: ${missingBoxes.map(box => box.boxData.boxId).join(', ')}. Por favor, descargue nuevamente el PDF del proveedor actualizado.`
      )
      onComplete()
      return
    }

    try {
      const mergedPdfBytes = await generateInterleavedPdfDocument(
        extractedBoxes,
        uploadedPdfBytes,
        boxesQuery.data
      )

      const filename = `${new Date().getTime()}-doble-etiquetado.pdf`
      downloadPdf(mergedPdfBytes, filename)

      message.success(`PDF generado para ${extractedBoxes.length} cajas con doble etiquetado`)
      setIsProcessing(false)
      onComplete()
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Error al procesar el PDF'
      message.error(errorMessage)
      // eslint-disable-next-line no-console
      console.warn(error)
      setIsProcessing(false)
      onComplete()
    }
  }, [boxesQuery.data, extractedBoxes, uploadedPdfBytes, onComplete])

  useEffect(() => {
    const dataKey = `${boxIds.join(',')}-${uploadedPdfBytes.byteLength}`

    if (!boxesQuery.data || boxesQuery.isLoading || hasGeneratedRef.current) return
    if (processedDataRef.current === dataKey) return

    hasGeneratedRef.current = true
    processedDataRef.current = dataKey

    processInterleavedPdf()

    return () => {
      hasGeneratedRef.current = false
    }
  }, [boxesQuery.data, boxesQuery.isLoading, boxIds, uploadedPdfBytes, processInterleavedPdf])

  return {
    isProcessing: isProcessing || boxesQuery.isLoading
  }
}
